import React, { useState, useEffect, useRef } from 'react';
import { Eye, EyeOff, Save, RefreshCw, Upload, X, Image as ImageIcon, Search } from 'lucide-react';
import { Base_Url, label, lableView } from '../../../../config/config';
import Sidebar from '../../../../component/b2c/sidebar/sidebar';
import NavBar from '../../../../component/b2c/navBar/navBar';
import { useMyContext } from '../../../../context/MyContext';
import '../../../../css/custom-scrollbar.css';
import { toast } from 'react-toastify';
import axios from 'axios';

const ShippingLabelCustomizer = () => {

    const { isDarkMode, isToggleSidebar } = useMyContext();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [brandLogoFile, setBrandLogoFile] = useState(null);
    const [brandLogoPreview, setBrandLogoPreview] = useState(null);
    const [existingLogo, setExistingLogo] = useState(null);
    const [placeholderLogo] = useState('/assets/images/logo1.png');

    // Preview zoom state/refs — controls the smaller inline preview + the large hover/click view
    const [isZoomOpen, setIsZoomOpen] = useState(false);
    const [previewHeight, setPreviewHeight] = useState(0);
    const labelRef = useRef(null);
    const PREVIEW_SCALE = 0.55;
    const LABEL_WIDTH = 480;

    const [visibilitySettings, setVisibilitySettings] = useState({
        brand_logo: true,
        shipping_address: true,
        shipping_pincode: true,
        payment_mode: true,
        shipment_type: true,
        invoice_amount: true,
        seller_name: true,
        seller_address: true,
        seller_pin: true,
        weight: true,
        product_name: true,
        price: true,
        return_address: true,
        default_logo: true
    });


    const fieldLabels = {
        brand_logo: 'Brand Logo (Uploaded)',
        shipping_address: 'Shipping Address',
        shipping_pincode: 'Shipping Pincode',
        payment_mode: 'Payment Mode',
        shipment_type: 'Shipment Type',
        invoice_amount: 'Invoice Amount',
        seller_name: 'Seller Name',
        seller_address: 'Seller Address',
        seller_pin: 'Seller Pin',
        weight: 'Weight',
        product_name: 'Product Name',
        price: 'Price',
        return_address: 'Return Address',
        default_logo: 'Default Logo'
    };

    const staticLabelData = {
        order_id: "***************",
        first_name: "First Name",
        last_name: "Last name",
        drop_address: "Drop Address ",
        drop_city: "Drop City",
        drop_state: "Drop State",
        drop_pin: "*******",
        cluster_code: "***",
        destination_code: "***",
        destination_location: "*****",
        payment_mode: "Payment Mode",
        shipping_mode: "Shipping Mode",
        cod_amount: "***.**",
        seller_name: "Seller name",
        seller_address: "Seller Address With Pincode",
        seller_pin: "******",
        weight: "***.***",
        partner: "Courier Parner name",
        product_desc: "Product Description",
        total_amount: "*****.**",
        waybill: "90********00",
        return_address: "I thum ********************"
    };

    useEffect(() => {
        const fetchLabelSettings = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem('token');
                const cusId = localStorage.getItem('cus_id');

                const response = await fetch(
                    'https://api.pickupxpress.com//V1/B2C/shipment/customize-label-view',
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'token': token || '',
                            'cus_id': cusId || ''
                        }
                    }
                );

                const result = await response.json();

                if (result.status === 'SUCCESS' && result.data) {
                    const data = result.data;

                    // Update visibility settings based on API response
                    setVisibilitySettings({
                        brand_logo: data.brand_logo === '1',
                        shipping_address: data.shipping_address === '1',
                        shipping_pincode: data.shipping_pincode === '1',
                        payment_mode: data.payment_mode === '1',
                        shipment_type: data.shipment_type === '1',
                        invoice_amount: data.invoice_amount === '1',
                        seller_name: data.seller_name === '1',
                        seller_address: data.seller_address === '1',
                        seller_pin: data.seller_pin === '1',
                        weight: data.weight === '1',
                        product_name: data.product_name === '1',
                        price: data.price === '1',
                        return_address: data.return_address === '1'
                    });

                    // Set existing logo if available
                    if (data.brand_logo_image) {
                        const logoUrl = data.brand_logo_image.startsWith('http')
                            ? data.brand_logo_image
                            : `https://api.pickupxpress.com/${data.brand_logo_image}`;
                        setExistingLogo(logoUrl);
                    } else {
                        setExistingLogo(null);
                    }
                } else {
                    setExistingLogo(null);
                }
            } catch (err) {
                console.error('Error fetching label settings:', err);
                toast.error('Error loading label settings');
                setExistingLogo(null);
            } finally {
                setLoading(false);
            }
        };

        fetchLabelSettings();
    }, []);

    // Recompute the natural (unscaled) height of the label whenever its content
    // could change, so the shrunken preview box sizes itself correctly.
    useEffect(() => {
        if (labelRef.current) {
            setPreviewHeight(labelRef.current.scrollHeight);
        }
    }, [visibilitySettings, brandLogoPreview, existingLogo, loading]);

    const handleCheckboxChange = (field) => {
        setVisibilitySettings(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setBrandLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setBrandLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const removeLogo = () => {
        setBrandLogoFile(null);
        setBrandLogoPreview(null);
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            const cusId = localStorage.getItem('cus_id');

            let dataToSend;
            let headers = {
                'token': token || '',
                'cus_id': cusId || ''
            };

            if (brandLogoFile) {
                const formData = new FormData();
                Object.keys(visibilitySettings).forEach(key => {
                    formData.append(key, visibilitySettings[key] ? '1' : '0');
                });
                formData.append('brand_logo_image', brandLogoFile);
                dataToSend = formData;
            } else {
                const jsonData = {};
                Object.keys(visibilitySettings).forEach(key => {
                    jsonData[key] = visibilitySettings[key] ? '1' : '0';
                });
                dataToSend = jsonData;
                headers['Content-Type'] = 'application/json';
            }

            const response = await axios.post(Base_Url + label, dataToSend, { headers });

            const result = response.data;
            if (result.status === "SUCCESS") {
                toast.success(result.message);
                setBrandLogoPreview(null);
                setBrandLogoFile(null);

                // Re-fetch to update existingLogo
                const updatedResponse = await fetch(
                    'https://api.pickupxpress.com//V1/B2C/shipment/customize-label-view',
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'token': token || '',
                            'cus_id': cusId || ''
                        }
                    }
                );

                const updatedResult = await updatedResponse.json();

                if (updatedResult.status === 'SUCCESS' && updatedResult.data) {
                    const data = updatedResult.data;
                    if (data.brand_logo_image) {
                        const logoUrl = data.brand_logo_image.startsWith('http')
                            ? data.brand_logo_image
                            : `https://api.pickupxpress.com/${data.brand_logo_image}`;
                        setExistingLogo(logoUrl);
                    }
                }
            } else {
                toast.error(result.message);
            }
        } catch (err) {
            toast.error('Error saving settings: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const toggleAll = (show) => {
        const newSettings = {};
        Object.keys(visibilitySettings).forEach(key => {
            newSettings[key] = show;
        });
        setVisibilitySettings(newSettings);
    };

    const formatWeight = () => {
        const weight = parseFloat(staticLabelData.weight);
        if (staticLabelData.partner?.toLowerCase() === 'bluedart') {
            return (weight / 1000).toFixed(2);
        }
        return weight.toFixed(2);
    };

    const getWeightUnit = () => {
        return staticLabelData.partner?.toLowerCase() === 'bluedart' ? 'KG' : 'Gram';
    };

    const Barcode = ({ value }) => (
        <svg width="220" height="56" viewBox="0 0 280 80" className="mx-auto">
            {Array.from({ length: 45 }).map((_, i) => {
                const width = Math.random() > 0.5 ? 2 : 4;
                const x = i * 6;
                return (
                    <rect
                        key={i}
                        x={x}
                        y="10"
                        width={width}
                        height="50"
                        fill="#000"
                    />
                );
            })}
            <text x="140" y="75" textAnchor="middle" fontSize="14" fontFamily="monospace" fontWeight="bold">
                {value}
            </text>
        </svg>
    );

    const isSellerSectionVisible = visibilitySettings.seller_name ||
        visibilitySettings.seller_address ||
        visibilitySettings.seller_pin;

    const isShippingAddressVisible = visibilitySettings.shipping_address ||
        visibilitySettings.shipping_pincode;

    const isPaymentSectionVisible = visibilitySettings.payment_mode ||
        visibilitySettings.shipment_type ||
        visibilitySettings.invoice_amount;

    const isProductSectionVisible = visibilitySettings.product_name ||
        visibilitySettings.price;

    // The label table markup itself — untouched from the original design.
    // Pulled into a variable so it can be reused for both the small inline
    // preview and the large hover/click zoom view without duplicating it.
    const labelMarkup = (
        <table className="w-full border-collapse">
            <tbody>
                <tr>
                    <td colSpan="3" className="border-2 border-black p-1.5 text-center bg-white">
                        <div className="flex justify-center items-center gap-4 mb-1">
                            {visibilitySettings.brand_logo && (
                                <>
                                    <div className="text-center">
                                        <p className="text-[8px] font-semibold text-gray-600 mb-0.5">Brand Logo</p>
                                        <img
                                            src={brandLogoPreview || existingLogo}
                                            alt="Brand Logo"
                                            className="h-8 mx-auto rounded p-0.5"
                                        />
                                    </div>
                                    <div className="h-10 w-px bg-gray-800"></div>
                                </>
                            )}
                            <div className="text-center">
                                <p className="text-[8px] font-semibold text-gray-600 mb-0.5">Default Logo</p>
                                <img
                                    src={placeholderLogo}
                                    alt="Default Logo"
                                    className="h-8 mx-auto rounded p-0.5"
                                />
                            </div>
                        </div>
                        <p className="font-bold text-[9px]">
                            Order: {staticLabelData.order_id}
                        </p>
                    </td>
                </tr>

                {(isShippingAddressVisible || isPaymentSectionVisible) && (
                    <tr>
                        {isShippingAddressVisible ? (
                            <td colSpan="2" className="border-2 border-black p-1 align-top text-[8px]">
                                {visibilitySettings.shipping_address && (
                                    <>
                                        <p className="font-bold mb-0.5">Shipping Address</p>
                                        <p>{staticLabelData.first_name} {staticLabelData.last_name}</p>
                                        <p>{staticLabelData.drop_address}</p>
                                        <p>{staticLabelData.drop_city}, {staticLabelData.drop_state}</p>
                                    </>
                                )}
                                {visibilitySettings.shipping_pincode && (
                                    <p className="font-bold mt-0.5">
                                        PIN: {staticLabelData.drop_pin}/{staticLabelData.cluster_code}/{staticLabelData.destination_code}/{staticLabelData.destination_location}
                                    </p>
                                )}
                            </td>
                        ) : (
                            <td colSpan="2" className="border-2 border-black p-1 align-top text-[8px]"></td>
                        )}
                        {isPaymentSectionVisible ? (
                            <td className="border-2 border-black p-1 align-top text-[8px]">
                                {visibilitySettings.payment_mode && (
                                    <p className="font-bold">{staticLabelData.payment_mode.toUpperCase()}</p>
                                )}
                                {visibilitySettings.shipment_type && (
                                    <p>{staticLabelData.shipping_mode}</p>
                                )}
                                {visibilitySettings.invoice_amount && (
                                    <p>INR {staticLabelData.cod_amount}</p>
                                )}
                            </td>
                        ) : (
                            <td className="border-2 border-black p-1 align-top text-[8px]"></td>
                        )}
                    </tr>
                )}

                {(isSellerSectionVisible || visibilitySettings.weight) && (
                    <tr>
                        {isSellerSectionVisible ? (
                            <td colSpan="2" className="border-2 border-black p-1 align-top text-[8px]">
                                {visibilitySettings.seller_name && (
                                    <p><span className="font-bold">Seller:</span> {staticLabelData.seller_name}</p>
                                )}
                                {visibilitySettings.seller_address && (
                                    <p><span className="font-bold">Address:</span> {staticLabelData.seller_address}</p>
                                )}
                                {visibilitySettings.seller_pin && (
                                    <p><span className="font-bold">Pin:</span> {staticLabelData.seller_pin}</p>
                                )}
                            </td>
                        ) : (
                            <td colSpan="2" className="border-2 border-black p-1 align-top text-[8px]"></td>
                        )}
                        {visibilitySettings.weight ? (
                            <td className="border-2 border-black p-1 align-top text-[8px]">
                                <p>
                                    <span className="font-bold">Weight ({getWeightUnit()}):</span><br />
                                    {formatWeight()}
                                </p>
                            </td>
                        ) : (
                            <td className="border-2 border-black p-1 align-top text-[8px]"></td>
                        )}
                    </tr>
                )}

                {isProductSectionVisible && (
                    <>
                        <tr>
                            {visibilitySettings.product_name ? (
                                <th className="border-2 border-black p-1 text-[8px] font-bold">Product</th>
                            ) : (
                                <th className="border-2 border-black p-1 text-[8px] font-bold"></th>
                            )}
                            {visibilitySettings.price ? (
                                <>
                                    <th className="border-2 border-black p-1 text-[8px] font-bold">Price</th>
                                    <th className="border-2 border-black p-1 text-[8px] font-bold">Total</th>
                                </>
                            ) : (
                                <>
                                    <th className="border-2 border-black p-1 text-[8px] font-bold"></th>
                                    <th className="border-2 border-black p-1 text-[8px] font-bold"></th>
                                </>
                            )}
                        </tr>
                        <tr>
                            {visibilitySettings.product_name ? (
                                <td className="border-2 border-black p-1 text-[8px]">
                                    {staticLabelData.product_desc}
                                </td>
                            ) : (
                                <td className="border-2 border-black p-1 text-[8px]"></td>
                            )}
                            {visibilitySettings.price ? (
                                <>
                                    <td className="border-2 border-black p-1 text-[8px]">
                                        INR {staticLabelData.total_amount}
                                    </td>
                                    <td className="border-2 border-black p-1 text-[8px]">
                                        INR {staticLabelData.total_amount}
                                    </td>
                                </>
                            ) : (
                                <>
                                    <td className="border-2 border-black p-1 text-[8px]"></td>
                                    <td className="border-2 border-black p-1 text-[8px]"></td>
                                </>
                            )}
                        </tr>
                    </>
                )}

                <tr>
                    <td colSpan="3" className="border-2 border-black p-1 text-center">
                        <Barcode value={staticLabelData.waybill} />
                        <p className="font-bold text-[8px]">
                            {staticLabelData.partner === 'ecom' ? 'Ecom Express' :
                                staticLabelData.partner.charAt(0).toUpperCase() + staticLabelData.partner.slice(1)}
                        </p>
                    </td>
                </tr>

                {visibilitySettings.return_address && (
                    <tr>
                        <td colSpan="3" className="border-2 border-black p-1 text-[8px]">
                            <p className="font-bold mb-0.5">Return Address</p>
                            <p>{staticLabelData.return_address}</p>
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    );
const glass =
    "backdrop-blur-2xl backdrop-saturate-150 bg-white/50 dark:bg-white/[0.06] " +
    "border border-white/60 dark:border-white/10 " +
    "shadow-[0_8px_32px_rgba(31,41,55,0.08)]";
    return (
        <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'bg-[#0f0b1f]' : 'bg-white'}`}>
          <div className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-indigo-200/30 dark:bg-indigo-500/10 blur-3xl" />
              
                      <NavBar />
                      <div className="flex flex-1 overflow-hidden">
                        {isToggleSidebar && (
                          <div className={`w-64 m-3 rounded-3xl ${glass} overflow-hidden`}>
                            <Sidebar />
                          </div>
                        )}

                {/* Main content: fixed to viewport height, no page-level scroll */}
                <div className="flex-1 min-h-0 flex flex-col bg-gray-200 p-3 md:p-4 overflow-hidden">

                    {/* Compact header */}
                    <div className="shrink-0 mb-2 flex items-center gap-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-600 shadow-md">
                            <ImageIcon color='white' className="text-white" size={16} />
                        </div>
                        <div>
                            <h1 className="text-base md:text-lg font-bold text-gray-900 leading-tight">
                                Shipping Label Customizer
                            </h1>
                            <p className="text-[10px] text-gray-500">
                                Toggle fields to customize how your label looks
                            </p>
                        </div>
                    </div>

                    {/* Two panels, each capped to available height, internal scroll only as a safety net */}
                    <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-2 gap-3">

                        {/* LEFT: Field toggles */}
                        <div className="min-h-0 flex flex-col rounded-xl border border-gray-200 bg-white shadow-sm p-3">
                            <div className="shrink-0 flex items-center justify-between mb-2">
                                <h2 className="text-xs font-semibold text-gray-800">
                                    Label Fields
                                </h2>
                                <div className="flex gap-1">
                                    <button
                                        onClick={() => toggleAll(true)}
                                        className="text-[10px] px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
                                    >
                                        Show All
                                    </button>
                                    <button
                                        onClick={() => toggleAll(false)}
                                        className="text-[10px] px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
                                    >
                                        Hide All
                                    </button>
                                </div>
                            </div>

                            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-1">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                    {Object.keys(fieldLabels).map((field) => (
                                        <div key={field} className={field === 'brand_logo' && visibilitySettings[field] ? 'sm:col-span-2' : ''}>
                                            <label className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg border border-gray-200 bg-white cursor-pointer transition hover:border-gray-400 hover:bg-gray-50">
                                                <span className="text-[10px] font-medium text-gray-700 truncate">
                                                    {fieldLabels[field]}
                                                </span>
                                                <div className="flex items-center gap-1.5 shrink-0">
                                                    {visibilitySettings[field] ? (
                                                        <Eye color="blue" className="w-3 h-3 text-gray-700" />
                                                    ) : (
                                                        <EyeOff className="w-3 h-3 text-gray-300" />
                                                    )}
                                                    <button
                                                        type="button"
                                                        role="switch"
                                                        aria-checked={visibilitySettings[field]}
                                                        onClick={() => handleCheckboxChange(field)}
                                                        className={`relative inline-flex h-4 w-8 shrink-0 items-center rounded-full transition-colors ${
                                                            visibilitySettings[field]
                                                                ? 'bg-emerald-500'
                                                                : 'bg-gray-300'
                                                        }`}
                                                    >
                                                        <span
                                                            className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${
                                                                visibilitySettings[field] ? 'translate-x-4.5' : 'translate-x-1'
                                                            }`}
                                                            style={{ transform: visibilitySettings[field] ? 'translateX(16px)' : 'translateX(3px)' }}
                                                        />
                                                    </button>
                                                    {/* Hidden native checkbox kept for a11y / form semantics, logic untouched */}
                                                    <input
                                                        type="checkbox"
                                                        checked={visibilitySettings[field]}
                                                        onChange={() => handleCheckboxChange(field)}
                                                        className="sr-only"
                                                    />
                                                </div>
                                            </label>

                                            {field === 'brand_logo' && visibilitySettings[field] && (
                                                <div className="mt-1.5 p-2 rounded-lg border border-dashed border-gray-300 bg-gray-50">
                                                    <label className="block text-[10px] font-medium text-gray-600 mb-1.5">
                                                        Upload Brand Logo
                                                    </label>

                                                    {brandLogoPreview ? (
                                                        <div className="relative inline-block">
                                                            <img
                                                                src={brandLogoPreview}
                                                                alt="Brand Logo Preview"
                                                                className="h-10 w-auto border border-gray-200 rounded bg-white p-0.5"
                                                            />
                                                            <button
                                                                onClick={removeLogo}
                                                                className="absolute -top-1.5 -right-1.5 bg-black text-white rounded-full p-0.5 hover:bg-gray-800"
                                                            >
                                                                <X className="w-2.5 h-2.5" />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <label className="flex items-center justify-center w-full h-10 rounded-lg border-2 border-dashed border-gray-300 cursor-pointer hover:border-gray-500 transition">
                                                            <div className="flex items-center gap-1.5 text-gray-500">
                                                                <Upload className="w-3.5 h-3.5" />
                                                                <span className="text-[10px]">Click to upload</span>
                                                            </div>
                                                            <input
                                                                type="file"
                                                                accept="image/*"
                                                                onChange={handleFileChange}
                                                                className="hidden"
                                                            />
                                                        </label>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="shrink-0 flex gap-2 mt-2 pt-2 border-t border-gray-200">
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="flex-1 flex items-center justify-center gap-1.5 bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-gray-800 disabled:opacity-60 transition text-[11px] font-medium"
                                >
                                    <Save className="w-3.5 h-3.5" />
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                                <button
                                    disabled={loading}
                                    className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-60 transition"
                                >
                                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                                </button>
                            </div>
                        </div>

                        {/* RIGHT: Label preview */}
                        <div className="min-h-0 flex flex-col rounded-xl border border-gray-200 bg-white shadow-sm p-2">
                            <h2 className="shrink-0 text-xs font-semibold text-gray-800 mb-2">
                                Label Preview
                            </h2>

                            <div className="flex-1 min-h-0 flex items-center justify-center overflow-hidden">
                                {/* Shrunk preview: fixed to a smaller box, hover reveals a magnifying-glass icon, click opens the large view */}
                                <div
                                    className="relative group cursor-zoom-in"
                                    style={{
                                        width: LABEL_WIDTH * PREVIEW_SCALE,
                                        height: previewHeight ? Math.min(previewHeight * PREVIEW_SCALE, 400) : undefined
                                    }}
                                    onClick={() => setIsZoomOpen(true)}
                                >
                                    <div
                                        ref={labelRef}
                                        style={{
                                            width: LABEL_WIDTH,
                                            transform: `scale(${PREVIEW_SCALE})`,
                                            transformOrigin: 'top left'
                                        }}
                                    >
                                        <div className="bg-white border-2 border-black rounded-lg p-1.5 w-full text-black">
                                            {labelMarkup}
                                        </div>
                                    </div>

                                    {/* Hover overlay with magnifying glass */}
                                    <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/0 group-hover:bg-black/40 opacity-0 group-hover:opacity-100 transition-all duration-200">
                                        <div className="bg-white rounded-full p-1.5 shadow-lg">
                                            <Search className="w-4 h-4 text-black" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Large zoomed-in view of the label, opened on click */}
            {isZoomOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
                    onClick={() => setIsZoomOpen(false)}
                >
                    <div
                        className="relative max-h-[90vh] overflow-y-auto custom-scrollbar"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setIsZoomOpen(false)}
                            className="absolute -top-3 -right-3 bg-white rounded-full p-1.5 shadow-lg hover:bg-gray-100 z-10"
                        >
                            <X className="w-4 h-4 text-black" />
                        </button>
                        <div
                            className="bg-white border-2 border-black rounded-lg p-1.5 text-black"
                            style={{ width: LABEL_WIDTH }}
                        >
                            {labelMarkup}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ShippingLabelCustomizer;